/**
 * The backup timeline: 48 hours of vzdump activity, one lane per PVE host,
 * with now at the right-hand edge.
 *
 * Time runs left to right and the present is pinned to the right edge, so the
 * question the panel answers — *did last night's backup run, and how long did
 * it take?* — is answered by looking at the same place every time.
 *
 * The window is anchored to the snapshot's `generatedAt` rather than to the
 * browser's clock. That keeps the bars and the axis derived from one instant,
 * and means the timeline visibly freezes when contact with Proxmox is lost
 * instead of quietly sliding a stale picture leftwards.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { BackupOutcome, BackupRun, Snapshot } from '@proxmon/shared';
import {
  buildLanes,
  summarise,
  tickSteps,
  timelineTicks,
  zoomBy,
  zoomLabel,
  ZOOM_MAX,
  ZOOM_MIN,
  type PlacedRun,
} from './backups.ts';
import { BackupReportDialog } from './BackupReport.tsx';
import { clockTime, dayTime, elapsed, relativeTime } from '../format.ts';
import { useStore } from '../state/store.ts';

/** Height of one row of bars inside a lane; lanes with overlapping runs stack. */
const BAR_ROW_HEIGHT = 13;
/**
 * How close to the right-hand edge still counts as watching the live edge.
 * Wide enough to survive the rounding a fractional device pixel ratio
 * introduces, narrow enough that a deliberate scroll into the past sticks.
 */
const FOLLOW_SLACK_PX = 24;

const OUTCOME_LABEL: Record<BackupOutcome, string> = {
  running: 'running',
  ok: 'completed',
  warnings: 'completed with warnings',
  failed: 'failed',
};

export function BackupTimeline({ snapshot }: { snapshot: Snapshot }) {
  const open = useStore((s) => s.backupsOpen);
  const toggle = useStore((s) => s.toggleBackups);
  const select = useStore((s) => s.select);
  const zoom = useStore((s) => s.timelineZoom);
  const setZoom = useStore((s) => s.setTimelineZoom);
  const scroller = useRef<HTMLDivElement | null>(null);
  /**
   * The bar whose report is open, as it was when it was clicked.
   *
   * Held by value rather than by id so the dialog survives the run ageing out
   * of the window while somebody is reading it; the live row from the current
   * snapshot is preferred whenever there still is one, which is what lets a
   * report re-read itself when the backup it describes finishes.
   */
  const [opened, setOpened] = useState<BackupRun | null>(null);
  /**
   * Whether the view is still following the live edge. It stops as soon as the
   * operator scrolls back into the past, so a poll landing every five seconds
   * does not drag them forward again halfway through reading Saturday night.
   */
  const followingNow = useRef(true);
  /**
   * Where to put the scroll after a zoom lands: the instant the operator
   * zoomed around, and how far into the viewport it should stay. Applied in a
   * layout effect because the track has not been re-measured until then.
   */
  const anchor = useRef<{ fraction: number; offsetPx: number } | null>(null);

  const { backups } = snapshot;
  const windowEnd = snapshot.generatedAt;
  const windowStart = windowEnd - backups.windowHours * 3_600_000;

  const lanes = useMemo(
    () => buildLanes(snapshot.hosts.map((host) => host.name), backups, windowStart, windowEnd),
    [snapshot.hosts, backups, windowStart, windowEnd],
  );
  const ticks = useMemo(() => timelineTicks(windowStart, windowEnd, zoom), [windowStart, windowEnd, zoom]);
  const summary = useMemo(() => summarise(backups), [backups]);
  const openRun = opened ? backups.runs.find((run) => run.upid === opened.upid) ?? opened : null;

  const trackWidth = Math.max(1, backups.windowHours) * zoom;

  const pinToNow = useCallback((element: HTMLDivElement | null): void => {
    // Over-shooting is fine: the browser clamps to the far right, which is now.
    if (element) element.scrollLeft = element.scrollWidth;
  }, []);

  /*
   * A callback ref rather than a mount effect, because the scroll container is
   * not always there on the first render — a snapshot that arrives before its
   * hosts do replaces this whole subtree, and a one-shot effect keyed on the
   * fold state would never fire again for it.
   */
  const attachScroller = useCallback(
    (element: HTMLDivElement | null): void => {
      scroller.current = element;
      if (followingNow.current) pinToNow(element);
    },
    [pinToNow],
  );

  /**
   * Zoom, keeping one instant where it was on screen.
   *
   * Without an anchor a zoom is a teleport: the track grows from its left edge
   * and whatever the operator was reading slides out of the viewport. The
   * instant to hold still is the one under the cursor for a wheel gesture, and
   * the middle of the viewport for the buttons — except while following the
   * live edge, which stays pinned to the right on its own.
   *
   * `anchorPx` is measured from the left edge of the scroll viewport.
   */
  const applyZoom = useCallback(
    (next: number, anchorPx?: number): void => {
      const element = scroller.current;
      if (next === zoom) return;
      if (element && !followingNow.current) {
        // The grid is exactly [lane names][track], so whatever the scroll
        // width has beyond the track is the sticky name column.
        const columnPx = element.scrollWidth - trackWidth;
        const offsetPx = anchorPx ?? element.clientWidth / 2;
        const intoTrack = element.scrollLeft + offsetPx - columnPx;
        // Clamped because the anchor can land on the sticky name column, which
        // is not a point in time at all.
        const fraction = Math.min(1, Math.max(0, intoTrack / trackWidth));
        anchor.current = { fraction, offsetPx };
      }
      setZoom(next);
    },
    [setZoom, trackWidth, zoom],
  );

  // Deliberately every render: a poll, an unfold, a host appearing, a window
  // that grew, a zoom. Whatever moved, the live edge goes back under the
  // operator's eye unless they have chosen to look at the past — and if they
  // have, a zoom puts the instant they zoomed around back where it was.
  useLayoutEffect(() => {
    const element = scroller.current;
    if (open && followingNow.current) {
      pinToNow(element);
      anchor.current = null;
      return;
    }
    if (element && anchor.current) {
      const { fraction, offsetPx } = anchor.current;
      const columnPx = element.scrollWidth - trackWidth;
      // Out-of-range is fine: the browser clamps, which is the honest answer
      // when the window's own edge is nearer than the anchor asked for.
      element.scrollLeft = fraction * trackWidth + columnPx - offsetPx;
      anchor.current = null;
    }
  });

  /*
   * Ctrl/⌘ + wheel zooms, which is also what a touchpad pinch arrives as. A
   * native non-passive listener because React's wheel handler cannot call
   * preventDefault, and without that the browser zooms the whole page instead.
   */
  useEffect(() => {
    const element = scroller.current;
    if (!element || !open) return;

    const onWheel = (event: WheelEvent): void => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      if (event.deltaY === 0) return;
      const next = zoomBy(zoom, event.deltaY < 0 ? 1 : -1);
      applyZoom(next, event.clientX - element.getBoundingClientRect().left);
    };

    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, [applyZoom, open, zoom]);

  // A resize changes how much of the window fits without re-rendering anything.
  useEffect(() => {
    const reflow = (): void => {
      if (followingNow.current) pinToNow(scroller.current);
    };
    window.addEventListener('resize', reflow);
    return () => window.removeEventListener('resize', reflow);
  }, [pinToNow]);

  /*
   * Why the panel might have nothing to draw. Said out loud rather than left as
   * an empty strip: "Proxmon is not looking" and "the cluster has no backups"
   * are very different answers.
   */
  const note = backups.disabled
    ? 'Task logs are not being read (PROXMON_BACKUP_WINDOW_HOURS=0), so Proxmon cannot say anything about backups.'
    : snapshot.hosts.length === 0
      ? 'No hosts in this snapshot, so there are no lanes to draw.'
      : null;

  /*
   * The lanes draw their gridlines with a repeating background, which starts at
   * the left edge of the track — but the window rarely opens exactly on a tick.
   * Shifting the pattern by the distance to the first one is what keeps those
   * lines under the axis marks instead of up to a whole step off.
   */
  const gridWidth = (tickSteps(zoom).stepMs / 3_600_000) * zoom;
  const gridOffset = (ticks[0]?.offset ?? 0) * trackWidth;

  return (
    <section className={`backups${open ? '' : ' folded'}`} aria-label="Cluster backups">
      <header>
        <button type="button" className="fold" onClick={toggle} aria-expanded={open} aria-controls="backup-body">
          <span className="chev" aria-hidden="true">
            ▾
          </span>
          <span className="title">Backups</span>
          <span className="window">
            {backups.disabled ? 'not polled' : `last ${backups.windowHours}h`}
          </span>
        </button>

        {/*
          Outside the fold button, because a button inside a button is not
          markup a browser will honour. Hidden when there is nothing to zoom.
        */}
        {open && !note && (
          <div className="zoom" role="group" aria-label="Timeline zoom">
            <button
              type="button"
              onClick={() => applyZoom(zoomBy(zoom, -1))}
              disabled={zoom <= ZOOM_MIN}
              aria-label="Zoom out"
              title="Zoom out — more hours on screen (ctrl/⌘ + scroll)"
            >
              −
            </button>
            {/* The interval the axis is marking: the thing the zoom changes. */}
            <span className="step" title="Interval between labelled marks on the axis">
              {zoomLabel(zoom)}
            </span>
            <button
              type="button"
              onClick={() => applyZoom(zoomBy(zoom, 1))}
              disabled={zoom >= ZOOM_MAX}
              aria-label="Zoom in"
              title="Zoom in — down to minutes (ctrl/⌘ + scroll)"
            >
              +
            </button>
          </div>
        )}

        <div className="summary">
          {summary.failed > 0 && (
            <span className="pill danger" title="vzdump tasks that ended with an error">
              {summary.failed} failed
            </span>
          )}
          {summary.warnings > 0 && (
            <span className="pill edge" title="finished, but PVE reported warnings — usually one guest in the job">
              {summary.warnings} with warnings
            </span>
          )}
          {summary.running > 0 && (
            <span className="pill accent" title="still running">
              {summary.running} running
            </span>
          )}
          {summary.ok > 0 && (
            <span className="pill ok" title="finished cleanly">
              {summary.ok} ok
            </span>
          )}
          {backups.unreadable.length > 0 && (
            <span
              className="pill danger"
              title={`Proxmon cannot read the task log on ${backups.unreadable.join(', ')}, so these hosts' backups are unknown — not absent.`}
            >
              {backups.unreadable.length} unreadable
            </span>
          )}
          <span className="last" title="when the most recent backup finished cleanly">
            last clean run {relativeTime(summary.lastSuccessAt)}
          </span>
        </div>
      </header>

      {/*
        Rendered even when folded, and hidden with the attribute rather than
        dropped from the tree, so the `aria-controls` above always points at
        something a screen reader can find.
      */}
      <div className="backup-body" id="backup-body" hidden={!open}>
        {note ? (
          <p className="empty">{note}</p>
        ) : (
          <div
            className="backup-scroll"
            ref={attachScroller}
            onScroll={(event) => {
              const element = event.currentTarget;
              followingNow.current =
                element.scrollLeft >= element.scrollWidth - element.clientWidth - FOLLOW_SLACK_PX;
            }}
          >
            <div
              className="backup-grid"
              style={
                {
                  '--track-width': `${trackWidth}px`,
                  '--grid-width': `${gridWidth}px`,
                  '--grid-offset': `${gridOffset}px`,
                } as CSSProperties
              }
            >
              <div className="corner" />
              <div className="axis">
                {ticks.map((tick) => (
                  <span
                    key={tick.at}
                    className={`tick${tick.midnight ? ' midnight' : ''}${tick.label ? ' labelled' : ''}`}
                    style={{ left: `${tick.offset * 100}%` }}
                  >
                    {tick.label && <span className="tick-label">{tick.label}</span>}
                  </span>
                ))}
                <span className="tick now">
                  <span className="tick-label">now</span>
                </span>
              </div>

              {lanes.map((lane) => (
                <Lane
                  key={lane.node}
                  lane={lane}
                  onSelectHost={() => select(`host:${lane.node}`)}
                  onSelectRun={(run) => {
                    // Both, deliberately: the report answers *which guest*,
                    // and the map selection behind it is where the operator
                    // lands when they close it.
                    setOpened(run);
                    select(guestNodeId(snapshot, run) ?? `host:${run.node}`);
                  }}
                  ticks={ticks}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {openRun && <BackupReportDialog run={openRun} onClose={() => setOpened(null)} />}
    </section>
  );
}

function Lane({
  lane,
  ticks,
  onSelectHost,
  onSelectRun,
}: {
  lane: ReturnType<typeof buildLanes>[number];
  ticks: ReturnType<typeof timelineTicks>;
  onSelectHost(): void;
  onSelectRun(run: BackupRun): void;
}) {
  const height = lane.rows * BAR_ROW_HEIGHT + 6;

  return (
    <>
      <button type="button" className="lane-name" style={{ height }} onClick={onSelectHost} title={laneTitle(lane)}>
        <span className="host">{lane.node}</span>
        {lane.storages.length > 0 && (
          <span className="store">
            {lane.storages.length === 1 ? lane.storages[0] : `${lane.storages.length} storages`}
          </span>
        )}
      </button>
      <div className="lane-track" style={{ height }}>
        {ticks
          .filter((tick) => tick.midnight)
          .map((tick) => (
            <span key={tick.at} className="day-line" style={{ left: `${tick.offset * 100}%` }} />
          ))}

        {lane.runs.map((placed) => (
          <Bar key={placed.run.upid} placed={placed} onSelect={() => onSelectRun(placed.run)} />
        ))}

        <span className="now-line" />

        {lane.runs.length === 0 && (
          <span className="lane-note">
            {lane.unreadable ? 'task log unreadable' : 'no backup in this window'}
          </span>
        )}
      </div>
    </>
  );
}

function Bar({ placed, onSelect }: { placed: PlacedRun; onSelect(): void }) {
  const { run } = placed;
  return (
    <button
      type="button"
      className={`run ${run.outcome}${placed.clippedStart ? ' clipped' : ''}`}
      style={{ left: `${placed.left * 100}%`, width: `${placed.width * 100}%`, top: placed.row * BAR_ROW_HEIGHT }}
      onClick={onSelect}
      title={describe(run)}
    >
      <span className="sr-only">{describe(run)}</span>
    </button>
  );
}

/** The tooltip, and the accessible name of the bar. */
function describe(run: BackupRun): string {
  const target = run.vmid === null ? 'all guests on the host' : `${run.guestName ?? 'guest'} (${run.vmid})`;
  const when =
    run.endedAt === null
      ? `started ${dayTime(run.startedAt)}, running for ${elapsed(Date.now() - run.startedAt)}`
      : `${dayTime(run.startedAt)} → ${clockTime(run.endedAt)} (${elapsed(run.endedAt - run.startedAt)})`;

  return [
    `${run.node}: backup of ${target} — ${OUTCOME_LABEL[run.outcome]}`,
    when,
    // Absent while the task's log has yet to be read, and for a job that names
    // a dumpdir rather than a storage. Saying nothing beats guessing.
    run.storage ? `to storage ${run.storage}` : null,
    run.status ? `PVE says: ${run.status}` : null,
    run.user ? `as ${run.user}` : null,
    // The bar is a button; say what pressing it does, for the tooltip and for
    // the screen reader reading the same string as its name.
    'Click for the guest-by-guest report',
  ]
    .filter(Boolean)
    .join('\n');
}

/**
 * The lane label's tooltip. It names every storage the host's runs went to,
 * because the label itself only has room to say how many when they disagree.
 */
function laneTitle(lane: ReturnType<typeof buildLanes>[number]): string {
  const where =
    lane.storages.length === 0 ? null : `backing up to ${lane.storages.join(', ')}`;
  return [`Show ${lane.node} on the map`, where].filter(Boolean).join('\n');
}

/**
 * The map id of the guest a run backed up, when this snapshot still has it.
 *
 * Backups are addressed by vmid, the map by `${kind}/${node}/${vmid}` — and a
 * guest that has been migrated or deleted since the backup ran has no place on
 * the map, so the caller falls back to the host.
 */
function guestNodeId(snapshot: Snapshot, run: BackupRun): string | null {
  if (run.vmid === null) return null;
  const guest = snapshot.guests.find((candidate) => candidate.vmid === run.vmid);
  return guest ? `guest:${guest.id}` : null;
}
